import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './SessionwisePieChart.css';
import ParticipationStatusPopup from '../ParticipationStatusPopup/ParticipationStatusPopup';
import tooltipTexts from '../../utils/tooltipTexts';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../api/apiClient';

ChartJS.register(ArcElement, Tooltip, Legend);

const getCssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const SessionwisePieChart = ({ vehicleId }) => {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [sessionData, setSessionData] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState('');
  const [hoveredStat, setHoveredStat] = useState(null);
  const chartRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('COMPLETE');

  // Reset and reload when vehicle changes
  useEffect(() => {
    console.log('Vehicle ID changed to:', vehicleId);
    setIsLoading(true);
    setChartData(null);
    setSessionData([]);
    setSelectedAttempt('');
    
    if (vehicleId) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [vehicleId]);

  // Update chart when attempt changes
  useEffect(() => {
    if (sessionData.length > 0 && selectedAttempt) {
      prepareChartData();
    }
  }, [selectedAttempt, sessionData]);

  const fetchData = async () => {
    try {
      const url = `${API_BASE_URL}/api/sessionwise-participation?vehicleId=${vehicleId}`;
      console.log('Fetching session data from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('Error fetching data:', response.status, response.statusText);
        return;
      }
      
      const data = await response.json();
      console.log('Received data for vehicle', vehicleId, ':', data);
      
      if (Array.isArray(data) && data.length > 0) {
        setSessionData(data);
        setSelectedAttempt(data[0].AttemptNumber.toString());
        console.log('Setting default attempt to:', data[0].AttemptNumber.toString());
      } else {
        console.log('No session data available for vehicle', vehicleId);
        setSessionData([]);
      }
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareChartData = () => {
    console.log('Preparing chart data for vehicle', vehicleId, 'attempt:', selectedAttempt);
    
    if (!vehicleId || !selectedAttempt) {
      return;
    }
    
    const selectedSession = sessionData.find(
      session => session.AttemptNumber.toString() === selectedAttempt && 
                session.vehicleID.toString() === vehicleId
    );
    
    console.log('Selected session:', selectedSession);
    
    if (!selectedSession) {
      console.log('No session found for the selected attempt');
      setChartData(null);
      return;
    }

    // Extract data from the database record
    const completedUsers = parseInt(selectedSession.completed_users || 0); 
    const incompleteUsers = parseInt(selectedSession.incomplete_users || 0);
    const notStartedUsers = parseInt(selectedSession.not_started_users || 0);
    
    const data = {
      labels: ['Completed', 'Incomplete', 'Not Started'],
      datasets: [
        {
          data: [completedUsers, incompleteUsers, notStartedUsers],
          backgroundColor: [
            '#3778c2', // blue for completed
            '#f1a939',  // amber for incomplete
            '#ced4da'  // light gray for not started
          ],
          hoverBackgroundColor: [
            '#2c609a', // darker blue
            '#d99527',  // darker amber
            '#adb5bd' // darker gray
          ],
          borderColor: [
            '#ffffff', 
            '#ffffff', 
            '#ffffff'
          ],
          borderWidth: 2,
          hoverBorderColor: '#ffffff',
          hoverBorderWidth: 2,
          borderRadius: 4,
          spacing: 3,
          offset: [0, 4, 0],
          cutout: '70%'
        }
      ]
    };

    setChartData(data);
  };

  const handleAttemptChange = (e) => {
    setSelectedAttempt(e.target.value);
  };

  // Calculate statistics for display
  const calculateStats = () => {
    if (!chartData) return { total: 0, percentages: [] };
    
    const data = chartData.datasets[0].data;
    const total = data.reduce((acc, val) => acc + val, 0);
    const percentages = data.map(value => total > 0 ? Math.round((value / total) * 100) : 0);
    
    return { total, percentages };
  };

  const { total, percentages } = calculateStats();

  // Prepare sessions dropdown options
  const getSessionOptions = () => {
    if (!sessionData.length) return [];
    
    return sessionData
      .filter(session => session.vehicleID.toString() === vehicleId)
      .map(session => ({
        value: session.AttemptNumber.toString(),
        label: `Session ${session.AttemptNumber}`
      }));
  };

  const sessionOptions = getSessionOptions();

  // Chart options
  const chartOptions = useMemo(() => {
    const cardBg = getCssVar('--bg-card', theme === 'dark' ? '#2a2d31' : '#ffffff');
    const textBody = getCssVar('--text-body', theme === 'dark' ? '#e1e1e6' : '#333333');
    const textMuted = getCssVar('--text-muted', theme === 'dark' ? '#a0a0a0' : '#555555');
    const border = getCssVar('--border', theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)');

    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      radius: '100%',
    layout: {
      padding: {
        top: 8,
        bottom: 8,
        left: 8,
        right: 8
      }
    },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          position: 'average',
          external: (context) => {
            const tooltip = context.tooltip;
            if (tooltip.opacity === 0) {
              return;
            }

            const centerX = context.chart.width / 2;
            const centerY = context.chart.height / 2;
            const distanceFromCenter = Math.sqrt(
              Math.pow(tooltip.caretX - centerX, 2) +
              Math.pow(tooltip.caretY - centerY, 2)
            );

            if (distanceFromCenter < 40) {
              tooltip.opacity = 0;
            }
          },
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (context) => {
              const value = context.raw || 0;
              const total = context.dataset.data.reduce((acc, current) => acc + current, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return ` ${value} users (${percentage}%)`;
            }
          },
          backgroundColor: cardBg,
          titleColor: textBody,
          bodyColor: textMuted,
          titleFont: { size: 13, weight: 'bold' },
          bodyFont: { size: 12 },
          displayColors: true,
          padding: 10,
          cornerRadius: 4,
          caretPadding: 40,
          borderColor: border,
          borderWidth: 1
        }
      },
      elements: {
        arc: {
          borderWidth: 2,
          borderColor: cardBg,
          hoverOffset: 5
        }
      },
      animation: {
        animateScale: true,
        animateRotate: true,
        duration: 800,
        easing: 'easeOutCubic'
      }
    };
  }, [theme]);

  const handleChartClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;

    const points = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false
    );

    if (points.length === 0) return;

    const clickedIndex = points[0].index;
    
    // Map index to status
    const statusMap = {
      0: 'COMPLETE',
      1: 'INCOMPLETE',
      2: 'NOT STARTED'
    };
    
    setSelectedStatus(statusMap[clickedIndex]);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };

  // Create a stat box component to display under the chart
  const StatBox = ({ label, value, percentage, color, index }) => {
    const isHovered = hoveredStat === index;
    
    return (
      <div 
        className={`sleek-stat-box ${isHovered ? 'sleek-stat-box-hovered' : ''}`}
        onMouseEnter={() => setHoveredStat(index)}
        onMouseLeave={() => setHoveredStat(null)}
        onClick={() => {
          setSelectedStatus(label.toUpperCase());
          setIsPopupOpen(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="sleek-stat-color" style={{ backgroundColor: color }}></div>
        <div className="sleek-stat-content">
          <div className="sleek-stat-label">{label}</div>
          <div className="sleek-stat-value">{value} <span className="sleek-stat-percentage">({percentage}%)</span></div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="chart-container sleek-chart-container">
        <div className="sleek-header">
          <h3>Session Participation
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.sessionParticipation}</span>
            </span>
          </h3>
        </div>
        <div className="sleek-content">
          <div className="sleek-loader"></div>
        </div>
      </div>
    );
  }

  if (!sessionData.length || sessionOptions.length === 0) {
    return (
      <div className="chart-container sleek-chart-container">
        <div className="sleek-header">
          <h3>Session Participation
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.sessionParticipation}</span>
            </span>
          </h3>
        </div>
        <div className="sleek-content sleek-empty-state">
          <div className="sleek-empty-icon">📊</div>
          <p className="sleek-empty-text">No session data available for this vehicle</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container sleek-chart-container">
      <div className="sleek-header">
        <h3>Session Participation
          <span className="info-tooltip">
            <span className="info-icon">i</span>
            <span className="tooltip-text">{tooltipTexts.sessionParticipation}</span>
          </span>
        </h3>
        <div className="sleek-controls">
          <select 
            value={selectedAttempt} 
            onChange={handleAttemptChange}
            className="sleek-select"
            aria-label="Select session"
          >
            {sessionOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {chartData ? (
        <div className="sleek-content">
          <div className="sleek-layout">
            <div className="sleek-chart-area">
              <div className="sleek-chart-wrapper">
                <Doughnut 
                  data={chartData} 
                  options={chartOptions} 
                  ref={chartRef}
                  onClick={handleChartClick}
                />
                <div className="sleek-chart-center">
                  <span className="sleek-center-value">{total}</span>
                  <span className="sleek-center-label">Total Users</span>
                </div>
              </div>
            </div>
            
            <div className="sleek-stats">
              <StatBox 
                label="Completed" 
                value={chartData.datasets[0].data[0]} 
                percentage={percentages[0]} 
                color={chartData.datasets[0].backgroundColor[0]} 
                index={0}
              />
              <StatBox 
                label="Incomplete" 
                value={chartData.datasets[0].data[1]} 
                percentage={percentages[1]} 
                color={chartData.datasets[0].backgroundColor[1]} 
                index={1}
              />
              <StatBox 
                label="Not Started" 
                value={chartData.datasets[0].data[2]} 
                percentage={percentages[2]} 
                color={chartData.datasets[0].backgroundColor[2]} 
                index={2}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="sleek-content">
          <p>Select a session to view data</p>
        </div>
      )}

      {isPopupOpen && (
        <ParticipationStatusPopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          vehicleId={vehicleId}
          sessionNumber={selectedAttempt}
          initialTab={selectedStatus}
        />
      )}
    </div>
  );
};

SessionwisePieChart.propTypes = {
  vehicleId: PropTypes.string.isRequired
};

export default SessionwisePieChart;

